import { Show, batch, createEffect, createSignal, onCleanup, onMount } from "solid-js";
import { addItemsToCart, addTocart, updatelinkedItemsProp } from "../js/cart";
import { convertSizeUnit } from "../js/utils";
import { getMeasureTypeOption } from "../js/variantsUtil";
import { getInvalidStandardSizes, getInvalidDIYMeasurements } from "../js/formValidation"
import StyleCustomizationModal from "./StyleCustomizationModal";
import { CustomLoader } from "../components/CustomLoader";
import { LazyPaymentIconsGroup } from "./LazyPaymentIconsGroup";
import { hash, removeHiphen, titleCase } from "../js/itemPropsHelper";
import { twMerge } from "tailwind-merge";
import { validateAccentData } from "../js/addToBagHelper";
import { NeumorphicAddToBagButton } from "./AddToBagButton";

const extractPropFromSelectedOption = (infoObj, prop) => {
  const options = infoObj.options;
  const selected = infoObj.selected;

  return options[selected][prop];
}

const getMeasurementValueStringify = (cmValue, inValue) => {
  return `${cmValue} cm / ${inValue} in`;
}

const getAdditionalInfos = (color, productInfo) => {
  const properties = {};

  const pantoneCode = extractPropFromSelectedOption(color, 'pantoneCode');
  if (pantoneCode != undefined && pantoneCode.trim().length > 0) {
    properties['_additional_infos'] = [
      {
        "name": "Pantone Code",
        "value": pantoneCode,
        "type": "text"
      }
    ]
  }

  if (properties['_additional_infos'] == undefined) properties['_additional_infos'] = [];

  const selectedColor = color.selected;
  const selectedOption = color.options[selectedColor];

  properties['_additional_infos'].push({
    "name": "Color Swatch",
    "value": selectedOption['swatchImages']['src'],
    "type": "image_url"
  })

  if (productInfo != undefined && productInfo["fabricSpec"] != undefined) {
    properties['_additional_infos'].push({
      "name": "Fabric",
      "value": productInfo['fabricSpec'],
      "type": "text"
    })
  }

  return properties;
}

const getMeasurementOptionProps = (measurementOptionsInfo, sendRefData, fitGroupId, REFERENCE_NOUN) => {
  const selectedOption = measurementOptionsInfo.selected;
  const measurementOptions = measurementOptionsInfo.options;

  const selectedMeasurementOption = measurementOptions[selectedOption]

  const sendRefOptions = sendRefData.radio.options;

  const properties = {
    "Measurement Mode": selectedMeasurementOption.label?.replaceAll('REFNOUN', REFERENCE_NOUN),
    "_measurement_option_id": selectedOption,
    "_measurement_option": selectedMeasurementOption.label?.replaceAll('REFNOUN', REFERENCE_NOUN),
    "_customer_message": selectedMeasurementOption.customerMessage?.replaceAll('REFNOUN', REFERENCE_NOUN),
    "_measurement_mode_message": selectedMeasurementOption?.subHeading?.replaceAll('REFNOUN', REFERENCE_NOUN),
    "_measurement_options": [
      ...Object.values(measurementOptions)?.map(option => ({
        "id": option.id,
        "measurement_option": option.label,
        "customer_message": option.customerMessage
      })),
      // ...Object.values(sendRefOptions)?.map(option => ({
      //   "id": option.id,
      //   "measurement_option": option.heading.replaceAll('REFNOUN', REFERENCE_NOUN),
      //   "customer_message": option.message.replaceAll('REFNOUN', REFERENCE_NOUN)
      // }))
    ],
    "_internal_info": [
      {
        "name": "Product Fit Group Id",
        "value": fitGroupId.product
      },
      {
        "name": "Measurement Fit Group Id",
        "value": fitGroupId[selectedMeasurementOption.measurementFitGroupId]
      }
    ]
  }

  return properties;
}

const getStyleCustomizationProps = (styleCustomizations) => {
  const properties = {
    "_is_customized": false,
    "_customized_styles": [],
    "_charged_styles": []
  };

  if (styleCustomizations != undefined && Object.keys(styleCustomizations).length > 0) {
    Object.values(styleCustomizations).forEach(styleCustomization => {
      const { label, selected, isCustomized = false, options } = styleCustomization;
      properties[label] = options[selected].label;
      const chargeVariant = options[selected]?.chargeVariant;
      if (chargeVariant) {
        properties._charged_styles.push(label);
      }

      if (isCustomized) {
        properties._is_customized = true;
        properties._customized_styles.push(label);
      }
    })
  }

  return properties;
}

const getAccentCustomizationProps = (accentCustomizations) => {
  const accentSettings = accentCustomizations.accentSettings;
  const accentFormData = accentCustomizations.accentFormData;

  if (accentSettings.options?.length < 1) return {};

  const properties = {
    Monogram: titleCase(removeHiphen(accentFormData.selected)),
    _monogram_info: []
  };

  const optionInputs = accentFormData?.option_inputs?.[accentFormData.selected]
  const accentSetting = accentSettings.options?.find(option => option.id == accentFormData.selected);

  Object.keys(optionInputs ?? {}).forEach(cKey => {
    const label = titleCase(removeHiphen(cKey));
    const value = optionInputs?.[cKey];

    properties._monogram_info.push({
      label,
      value,
      image_url: accentSetting.fields?.[cKey]?.options?.find(option => option.id == value)?.image ?? undefined,
      hexcode: accentSetting.fields?.[cKey]?.options?.find(option => option.id == value)?.hex ?? undefined
    })
    properties[`Monogram ${label}`] = value
  })

  return properties;
}

function getCustomizationItems(styleCustomizations) {
  const customizationItems = [];

  if (styleCustomizations != undefined && Object.keys(styleCustomizations).length > 0) {
    Object.values(styleCustomizations).forEach(styleCustomization => {
      const { selected, options } = styleCustomization;
      const option = options[selected]
      const chargeVariant = option?.chargeVariant;
      if (!chargeVariant) return;

      const isOneTimeChargePerItem = option?.oneTimeChargePerItem ?? false
      const item = customizationItems.find(item => item.variant_id == chargeVariant.id)

      if (isOneTimeChargePerItem && item) return;

      customizationItems.push({
        variant_id: chargeVariant.id,
        properties: {
          _type: "additional_charge",
          _group_id: "style",
          _price: `${chargeVariant.price / 100}`
        }
      })
    })
  }

  return customizationItems;
}

function getAccentItems(accentSettings, accentFormData) {
  const accentItems = [];

  const option = accentSettings.options.find(option => option.id == accentFormData.selected);
  const chargeVariant = option?.chargeVariant;
  if (chargeVariant) {

    const chargeVariants = Object.entries(option?.fields ?? {})
      .map(([fieldId, fieldInfo]) => {
        const fieldSelected = accentFormData.option_inputs?.[accentFormData.selected]?.[fieldId];
        const option = fieldInfo?.options?.find(option => option.id == fieldSelected);

        return option?.chargeVariant;
      })?.filter(variant => variant);

    const summary = Object.values(accentFormData.option_inputs?.[option.id] ?? {})
      .join(', ');

    accentItems.push({
      variant_id: chargeVariant.id,
      properties: {
        _type: "additional_charge",
        _group_id: "accents",
        Text: summary,
        // ...(accentFormData.option_inputs?.[option.id] ?? {}),
        _price: `${chargeVariant.price / 100}`
      }
    })

    chargeVariants.forEach(variant => {
      accentItems.push({
        variant_id: variant.id,
        properties: {
          _type: "additional_charge",
          _group_id: "accents",
          Text: summary,
          // ...(accentFormData.option_inputs?.[option.id] ?? {}),
          _price: `${variant?.price / 100}`
        }
      })
    })
  }

  return accentItems;
}

const getDefaultProperties = ({
  color,
  fit,
  sendRefData,
  measurementOptionsInfo,
  styleCustomizations,
  accentCustomizations,
  productInfo,
  fitGroupId,
  PRODUCT_REFERENCE_NOUN
}) => {
  const properties = {
    "Color": extractPropFromSelectedOption(color, 'label'),
    "Fit": extractPropFromSelectedOption(fit, 'label'),
    ...getMeasurementOptionProps(measurementOptionsInfo, sendRefData, fitGroupId, PRODUCT_REFERENCE_NOUN),
    ...getStyleCustomizationProps(styleCustomizations),
    ...getAdditionalInfos(color, productInfo),
    ...getAccentCustomizationProps(accentCustomizations)
  };

  return properties;
}

const getGradedSpecByFit = (gradedSpecMF, fitSelected) => {
  return gradedSpecMF['fit'].find(fitObj => handleize(fitObj['name']) === fitSelected);
}

const getCommaSepSeries = (arr) => {
  const tempArr = JSON.parse(JSON.stringify(arr));
  if (tempArr.length == 1) return tempArr[0]

  const last = tempArr.pop();

  return tempArr.join(', ') + ', and ' + last;
}

const getBaseSizeForStandardSizeOption = (standardSizeData) => {
  const {
    measurements: {
      anchorGraded
    }
  } = standardSizeData;

  return Object.values(anchorGraded)[0]['selected'];
}

const getStandardSizeMeasurements = (fitSelected, standardSizeData, gradedSpecMF, measurementsEnabled) => {
  const {
    measurementsToAvoid,
    measurements: {
      nonAnchorGraded,
      nonAnchorUngraded
    }
  } = standardSizeData;

  const measurementsToAvoidHandleized = measurementsToAvoid.map(measurement => handleize(measurement));
  const measurementsNeeded = measurementsEnabled.filter(x => !measurementsToAvoidHandleized.includes(x));
  const baseSizeHandleized = handleize(getBaseSizeForStandardSizeOption(standardSizeData));

  const baseUnitHandleized = handleize(gradedSpecMF['unit'] || "")
  const gradedSpec = getGradedSpecByFit(gradedSpecMF, fitSelected)?.['size']?.[baseSizeHandleized]

  const measurements = [];

  gradedSpec?.forEach(measurementObj => {
    const { label, value, enabled } = measurementObj;
    const labelHandleized = handleize(label);
    let measurementValue;

    if (!measurementsNeeded.includes(labelHandleized) || !enabled) return;
    if (nonAnchorGraded[labelHandleized] != undefined) {
      measurementValue = parseFloat(`${nonAnchorGraded[labelHandleized]['selected']}`?.replace(/-/g, '.'))
    } else if (nonAnchorUngraded[labelHandleized] != undefined) {
      measurementValue = parseFloat(`${nonAnchorUngraded[labelHandleized]['selected']}`?.replace(/-/g, '.'))
    } else {
      measurementValue = parseFloat(`${value}`);
    }

    const cmValue = convertSizeUnit(baseUnitHandleized, "cm", measurementValue);
    const inValue = convertSizeUnit(baseUnitHandleized, 'in', measurementValue);

    measurements.push({
      "name": label,
      "value": getMeasurementValueStringify(cmValue, inValue)
    })
  })

  return measurements;
}

const getStandardSizeProperties = ({
  color,
  fit,
  measurementOptionsInfo,
  styleCustomizations,
  accentCustomizations,
  sendRefData,
  standardSizeData,
  gradedSpecMF,
  measurementsEnabled,
  productInfo,
  fitGroupId,
  PRODUCT_REFERENCE_NOUN
}) => {

  const properties = getDefaultProperties({
    color,
    fit,
    sendRefData,
    measurementOptionsInfo,
    styleCustomizations,
    accentCustomizations,
    productInfo,
    fitGroupId,
    PRODUCT_REFERENCE_NOUN
  })

  const baseSize = getBaseSizeForStandardSizeOption(standardSizeData)
  const measurements = getStandardSizeMeasurements(fit.selected, standardSizeData, gradedSpecMF, measurementsEnabled);

  properties["Size"] = `${baseSize}`.toUpperCase();
  properties['_measurements'] = measurements;

  return properties;
}

const getBaseSizeForDIY = (anchorLabelHandleized, anchorValue, gradedSpec) => {

  let i = 0;
  const sizeLength = Object.keys(gradedSpec).length;

  for (const [baseSize, measurementsArr] of Object.entries(gradedSpec)) {
    const anchorObj = measurementsArr.find(measurement => handleize(measurement.label) === anchorLabelHandleized);
    if (anchorObj == undefined) {
      i++;
      continue;
    }

    const { startRange, endRange } = anchorObj;

    if (i === 0) {
      if (anchorValue < startRange) {
        return baseSize.toUpperCase();
      }
    } else if (i === sizeLength) {
      if (anchorValue > endRange) {
        return baseSize.toUpperCase();
      }
    }

    if (anchorValue >= startRange && anchorValue < endRange) {
      return baseSize.toUpperCase();
    }

    i++;
  }

}

const getDIYMeasurementsWithBaseSize = (diyData, gradedSpec, baseUnit, measurementsInfo) => {
  const properties = {};
  // const anchorValuesBySize = getAnchorValuesBySize(gradedSpec);
  let baseSize;

  Object.values(diyData.measurements)?.forEach(measurement => {
    const { label, value, isEnabled } = measurement;

    if (!isEnabled) return;

    const labelHandleized = handleize(label);
    const isAnchor = measurementsInfo[labelHandleized]['isAnchor'];

    if (isAnchor) {
      baseSize = getBaseSizeForDIY(labelHandleized, baseUnit == "cm" ? value.cm : value.in, gradedSpec, baseUnit);
      properties['Size'] = `${baseSize}`.toUpperCase();
    }

    const { cm: cmValue, in: inValue } = value;

    properties[label] = getMeasurementValueStringify(cmValue, inValue);
  })
  return properties;
}

const getVariantId = (colorSelected, measureTypeOption, productInfo) => {
  const { id } = productInfo['variants'][measureTypeOption][colorSelected];
  return id;
}

const getDIYProperties = ({
  color,
  fit,
  measurementOptionsInfo,
  styleCustomizations,
  accentCustomizations,
  gradedSpecMF,
  sendRefData,
  diyData,
  measurementsInfo,
  canAddMeasurements,
  productInfo,
  fitGroupId,
  PRODUCT_REFERENCE_NOUN
}) => {

  const gradedSpec = getGradedSpecByFit(gradedSpecMF, fit.selected)?.size;
  const measurementsWithBaseSize = canAddMeasurements
    ? getDIYMeasurementsWithBaseSize(diyData, gradedSpec, gradedSpecMF.unit, measurementsInfo)
    : {}

  const properties = {
    ...getDefaultProperties({
      color,
      fit,
      sendRefData,
      measurementOptionsInfo,
      styleCustomizations,
      accentCustomizations,
      productInfo,
      fitGroupId,
      PRODUCT_REFERENCE_NOUN
    }),
    ...measurementsWithBaseSize
  }


  return properties;
}

const getSendRefMeasurementOptionProps = (sendRefData, measurementOptionsInfo) => {
  const sendRefSelected = sendRefData.radio.selected;

  const sendRefOptions = sendRefData.radio.options;
  const sendRefSelectedOption = sendRefOptions[sendRefSelected];

  const measurementOptions = measurementOptionsInfo.options;

  const properties = {
    "_measurement_option_id": sendRefSelected,
    "_measurement_option": sendRefSelectedOption['heading'].replaceAll('REFNOUN', REFERENCE_NOUN),
    "_customer_message": sendRefSelectedOption['message'].replaceAll('REFNOUN', REFERENCE_NOUN),
    "_measurement_mode_message": sendRefSelectedOption['subHeading']?.replaceAll('REFNOUN', REFERENCE_NOUN),
    "_measurement_options": [
      ...Object.values(sendRefOptions)?.map(option => ({
        "id": option.id,
        "measurement_option": option.heading.replaceAll('REFNOUN', REFERENCE_NOUN),
        "customer_message": option.message.replaceAll('REFNOUN', REFERENCE_NOUN)
      })),
      ...Object.values(measurementOptions)?.filter(option => option.id != sendRefSelected)?.map(option => ({
        "id": option.id,
        "measurement_option": option.label,
        "customer_message": option.customerMessage
      }))
    ]
  }

  return properties;
}

const getSendRefProperties = ({
  color,
  fit,
  sendRefData,
  measurementOptionsInfo,
  styleCustomizations,
  accentCustomizations,
  productInfo,
  fitGroupId,
  PRODUCT_REFERENCE_NOUN
}) => {

  const properties = {
    ...getDefaultProperties({
      color,
      fit,
      sendRefData,
      measurementOptionsInfo,
      styleCustomizations,
      accentCustomizations,
      productInfo,
      fitGroupId,
      PRODUCT_REFERENCE_NOUN
    }),
    // ...getSendRefMeasurementOptionProps(sendRefData, measurementOptionsInfo)
  }

  return properties;
}

const getVirtualConsultationProperties = ({
  color,
  fit,
  sendRefData,
  measurementOptionsInfo,
  styleCustomizations,
  accentCustomizations,
  productInfo,
  fitGroupId,
  PRODUCT_REFERENCE_NOUN
}) => {

  const properties = {
    ...getDefaultProperties({
      color,
      fit,
      sendRefData,
      measurementOptionsInfo,
      styleCustomizations,
      accentCustomizations,
      productInfo,
      fitGroupId,
      PRODUCT_REFERENCE_NOUN
    })
  }

  return properties;
}

const getMadeToMeasureProperties = ({
  color,
  fit,
  sendRefData,
  measurementOptionsInfo,
  styleCustomizations,
  accentCustomizations,
  productInfo,
  fitGroupId,
  PRODUCT_REFERENCE_NOUN
}) => {

  const properties = {
    ...getDefaultProperties({
      color,
      fit,
      sendRefData,
      measurementOptionsInfo,
      styleCustomizations,
      accentCustomizations,
      productInfo,
      fitGroupId,
      PRODUCT_REFERENCE_NOUN
    })
  }

  return properties;
}

export const HeroFormSubmitGroup = (props) => {

  const {
    color: {
      color
    },
    fit: {
      fit
    },
    styleCustomizations: {
      styleCustomizations,
      handlers: {
        handleStyleChangeByColor
      }
    },
    accentCustomizations: {
      accentSettings,
      accentFormData,
      handlers: {
        clearAccentCustomization
      }
    },
    baseDimensionsMF,
    sizeSettings: {
      sizeData,
      sendRefData,
      standardSizeData,
      measurementOptions,
      measurementOptionsV2,
      measurementsInfo,
      diyData,
      measurementStatus,
      MEAS_VALIDATION_STATUS,
      errorMessage,
      handlers: {
        handleErrorMessage
      }
    },
    productInfo,
    fitGroupId,
    PRODUCT_REFERENCE_NOUN,
    sizeOption: {
      sizeOption
    },
    getters: {
      getExperimentFlags
    },
    heroSectionSettings
  } = props?.context();

  const today = new Date();
  const presaleEnabled = productInfo?.presale?.enabled;
  const presaleCloseDate = productInfo?.presale?.end_date;

  const showPreSale = () => {
    if (presaleEnabled == undefined || presaleEnabled == null || presaleEnabled == false) return false;
    if (presaleCloseDate == undefined || presaleCloseDate == null || today.getTime() > presaleCloseDate.getTime()) return false;

    return true;
  }

  const selectedColor = () => color().selected;
  const productHandle = productInfo?.handle

  const [addToCartLoading, setAddToCartLoading] = createSignal(false);
  const [buyNowLoading, setBuyNowLoading] = createSignal(false);

  const handleLoading = (option, isLoading) => {
    switch (option) {
      case 'addToCart':
        setAddToCartLoading(isLoading);
        break
      case 'buyNow':
        setBuyNowLoading(isLoading)
        break
    }
  }

  const colorSelected = () => color().selected;
  const isOutOfStock = () => color().options[colorSelected()].isOutOfStock;
  window.isSelectedColorOutOfStock = isOutOfStock;

  const updatedMeasurementOptions = () => {
    const updatedMeasurementOps = {
      "selected": "",
      "options": {
        ...measurementOptions()?.options,
        ...measurementOptionsV2?.options
      }
    };

    if (sizeOption() != undefined && sizeOption() == "NEW") {
      updatedMeasurementOps['selected'] = measurementOptionsV2?.selected;
    } else {
      updatedMeasurementOps['selected'] = measurementOptions().selected;
    }

    return updatedMeasurementOps;
  }


  const handleSubmit = async (option, callback = null) => {
    switch (option) {
      case "addToCart":
      case "buyNow":
        const measurementOptionSelected = updatedMeasurementOptions().selected;
        const redirect = option == "buyNow";

        if (measurementOptionSelected.trim().length < 1) {
          const message = 'Please select a size to proceed';

          if (errorMessage() != message) {
            handleErrorMessage(message);
          }

          return false;
        }

        const accentValidation = validateAccentData(accentFormData, accentSettings);
        if (accentValidation) {
          handleErrorMessage(accentValidation);
          return false;
        }

        let properties;
        const measurementsEnabled = Object.values(diyData().measurements).filter(measurement => measurement.isEnabled).map(measurement => measurement.id || handleize(measurement.label))

        switch (measurementOptionSelected) {
          case "standard-size":
            const invalidStandardSizeMeasurements = getInvalidStandardSizes(standardSizeData());

            if (invalidStandardSizeMeasurements.length > 0) {
              const message = 'Please select an inseam length to proceed';

              if (errorMessage() != message) {
                handleErrorMessage(message);
              }
              return false;
            }

            properties = getStandardSizeProperties({
              "color": color(),
              "fit": fit(),
              "measurementOptionsInfo": updatedMeasurementOptions(),
              "styleCustomizations": styleCustomizations(),
              "accentCustomizations": {
                accentSettings,
                accentFormData
              },
              "sizeData": sizeData(),
              "sendRefData": sendRefData(),
              "standardSizeData": standardSizeData(),
              "gradedSpecMF": baseDimensionsMF,
              "measurementsEnabled": measurementsEnabled,
              productInfo,
              fitGroupId,
              PRODUCT_REFERENCE_NOUN
            });
            break;
          case "measure-it-yourself":
            const invalidDIYMeasurements = getInvalidDIYMeasurements(diyData().measurements, measurementStatus(), MEAS_VALIDATION_STATUS);

            if (invalidDIYMeasurements.length > 0) {
              const invalidDIYMeasurementsJoined = getCommaSepSeries(invalidDIYMeasurements)
              const principleAuxiliary = invalidDIYMeasurements.length > 1 ? "are" : "is";
              const message = `Some measurement are invalid.<br> Please correct before proceeding.`

              if (errorMessage() != message) {
                handleErrorMessage(message);
              }

              // showErrorMessage(`Please Check Measure it yourself option, some measurements are not provided`);
              return false;
            }

            properties = getDIYProperties({
              "color": color(),
              "fit": fit(),
              "measurementOptionsInfo": updatedMeasurementOptions(),
              "styleCustomizations": styleCustomizations(),
              "accentCustomizations": {
                accentSettings,
                accentFormData
              },
              "diyData": diyData(),
              "sendRefData": sendRefData(),
              "gradedSpecMF": baseDimensionsMF,
              "measurementsInfo": measurementsInfo,
              "canAddMeasurements": invalidDIYMeasurements.length == 0,
              productInfo,
              fitGroupId,
              PRODUCT_REFERENCE_NOUN
            });
            break;
          case "send-us-garment":
          case "we-arrange-pickup":
          case "send-fitting-garment":
            properties = getSendRefProperties({
              "color": color(),
              "fit": fit(),
              "sendRefData": sendRefData(),
              "measurementOptionsInfo": updatedMeasurementOptions(),
              "styleCustomizations": styleCustomizations(),
              "accentCustomizations": {
                accentSettings,
                accentFormData
              },
              productInfo,
              fitGroupId,
              PRODUCT_REFERENCE_NOUN
            });
            break;
          case "made-to-measure":
            properties = getMadeToMeasureProperties({
              "color": color(),
              "fit": fit(),
              "sendRefData": sendRefData(),
              "measurementOptionsInfo": updatedMeasurementOptions(),
              "styleCustomizations": styleCustomizations(),
              "accentCustomizations": {
                accentSettings,
                accentFormData
              },
              productInfo,
              fitGroupId,
              PRODUCT_REFERENCE_NOUN
            })
            break;
          case "virtual-consultation":
            properties = getVirtualConsultationProperties({
              "color": color(),
              "fit": fit(),
              "sendRefData": sendRefData(),
              "measurementOptionsInfo": updatedMeasurementOptions(),
              "styleCustomizations": styleCustomizations(),
              "accentCustomizations": {
                accentSettings,
                accentFormData
              },
              productInfo,
              fitGroupId,
              PRODUCT_REFERENCE_NOUN
            });
            break;
        }

        properties["_experiment_key"] = window.experimentFlags;
        let customerMetafieldValue;
        if (typeof customerMetafield == "undefined") {
          customerMetafieldValue = null
        } else {
          customerMetafieldValue = customerMetafield
        }

        const measureTypeOption = getMeasureTypeOption(measurementOptionSelected, updatedMeasurementOptions().options, customerMetafieldValue, sendRefData());

        const variantId = getVariantId(color().selected, measureTypeOption, productInfo)

        handleLoading(option, true);
        const addToCartCallback = async (itemKey) => {
          // const customizationItems = getCustomizationItems(styleCustomizations());
          // const itemKeys = await Promise.all(customizationItems.map(item => addTocart(item.variant_id, {
          //   ...item.properties,
          //   _linked_items: [itemKey]
          // }, 1, false, () => { }, option == "buynow")));

          // await updatelinkedItemsProp(itemKey, itemKeys)

          batch(() => {
            handleLoading(option, false);
            handleErrorMessage("");
          })
        }
        if (option == "buyNow") {
          pushInitiateCheckout();
        }
        const customizationItems = getCustomizationItems(styleCustomizations());
        const accentItems = getAccentItems(accentSettings, accentFormData);

        const itemKeys = await Promise.all([...(customizationItems ?? []), ...(accentItems ?? [])].map(async item => hash(JSON.stringify({ variantId: item.variant_id, properties: item.properties }))))

        const itemKey = await hash(JSON.stringify({ variantId, properties }))

        const newCartItems = [];
        let i = 0;
        for (const item of [...(customizationItems ?? []), ...(accentItems ?? [])]) {
          const properties = {
            ...item.properties,
            _linked_items: [itemKey],
            _key: itemKeys[i++],
            _one_time_charge_per_item: undefined
          }

          // await addTocart(item.variant_id, properties, 1, false, () => { }, option == "buynow", {});
          newCartItems.push({
            id: item.variant_id,
            properties,
            quantity: 1
          })
        }

        // await addTocart(variantId, {
        //   ...properties,
        //   _linked_items: itemKeys,
        //   _key: itemKey,
        //   _additional_charges: {
        //     style: customizationItems.reduce((prev, item) => prev + (item.properties._price ?? 0), 0),
        //     accents: accentItems.reduce((prev, item) => prev + (item.properties._price ?? 0), 0)
        //   }
        // }, 1, redirect, addToCartCallback, option == "buyNow", {
        //   isQuickShop: props?.isModal == true,
        //   ...(props?.isModal == true ? props?.customProps : {})
        // });

        newCartItems.push({
          id: variantId,
          properties: {
            ...properties,
            _linked_items: itemKeys,
            _key: itemKey,
            _additional_charges: {
              style: customizationItems.reduce((prev, item) => prev + (parseFloat(`${item.properties._price}`) || 0), 0),
              accents: accentItems.reduce((prev, item) => prev + (parseFloat(`${item.properties._price}`) || 0), 0)
            }
          },
          quantity: 1
        })

        await addItemsToCart(newCartItems, redirect, addToCartCallback, option == "buyNow", {
          isQuickShop: props?.isModal == true,
          ...(props?.isModal == true ? props?.customProps : {})
        })

        break;
      case "notifyMe":
        const productName = productInfo.title;
        const colorName = color().options[colorSelected()]['label'];
        const imageUrl = color().options[colorSelected()]['swatchImages']['src'] + '&width=58'
        const notifyMeModal = document.getElementById('notify-me-modal');

        const notifyMeHeading = document.getElementById('notify-me-heading');
        notifyMeHeading.innerHTML = notifyMeHeading.innerHTML.replace('{~product-name-placeholder~}', `${productName} - ${colorName}`);

        const notifyMeHeadings = document.getElementsByClassName('notify-me-heading');
        for (const heading of notifyMeHeadings) {
          heading.innerHTML = notifyMeHeading.innerHTML;
        }

        const notifyBody = document.getElementById('notify-tags');
        notifyBody.value = `${productName} - ${colorName}`

        const notifyMeImage = document.getElementById('notify-me-image');
        notifyMeImage.src = imageUrl;

        notifyMeModal.classList.remove('hidden')
        notifyMeModal.classList.add('flex')

        break;
    }

    if (callback) {
      callback();
    }

    if (option == "addToCart") {
      window.openCartModal();
    }

    handleStyleChangeByColor(color()?.options?.[color()?.selected]?.defaultStyles ?? {})
    clearAccentCustomization()
    return true;
  }

  window.handleAddToBagClick = handleSubmit;

  const [isSubmitButtonVisible, setIsSubmitButtonVisible] = createSignal(true);
  const [isBeyondScrollBuffer, setIsBeyondScrollBuffer] = createSignal(false);

  let ref, subRef, topRef, observer, topObserver;

  createEffect(() => {
    if (window.innerWidth < 768) {
      if (!observer) {
        observer = new IntersectionObserver((entries) => {
          setIsSubmitButtonVisible(entries[0].isIntersecting);
        }, { threshold: 1 });
        if (subRef) observer.observe(subRef);
      }

      if (!topObserver) {
        topObserver = new IntersectionObserver((entries) => {
          setIsBeyondScrollBuffer(!entries[0].isIntersecting);
        }, { threshold: 0 });
        if (topRef) topObserver.observe(topRef);
      }
    }
    onCleanup(() => {
      observer?.disconnect();
      topObserver?.disconnect();
    });
  });

  createEffect(() => {
    if (window.innerWidth >= 768 || !ref) return;

    const whatsappChatButton = document.querySelector('#whatsappChatButton');
    const videoFloaterContainer = document.querySelector('#video-floater-container');
    const buttons = ref.getElementsByTagName('button');

    if (!isSubmitButtonVisible() && isBeyondScrollBuffer()) {
      if (subRef.getBoundingClientRect().top <= 0) { // scroll down
        subRef.style.height = `${ref.clientHeight}px`;
        ref.classList.add('fixed', 'top-7', 'left-0', 'w-full', 'z-[55]', 'p-1', 'pb-0', 'bg-canvas-light', 'border-b-outer', 'border-frame', 'justify-center')
        ref.classList.remove('relative', 'bottom-0', 'max-w-lg', 'border', 'mx-2', 'sm:mx-6', 'p-2');
        whatsappChatButton.classList.remove('bottom-14')
        videoFloaterContainer?.classList?.remove('bottom-14')
      } else { // scroll up
        ref.classList.add('fixed', 'bottom-0', 'left-0', 'w-full', 'z-[55]', 'p-1', 'pb-0', 'bg-canvas-light', 'border-t-outer', 'border-frame', 'justify-center')
        ref.classList.remove('relative', 'top-7', 'max-w-lg', 'border', 'mx-2', 'sm:mx-6', 'p-2');
        subRef.style.height = "";
        whatsappChatButton.classList.add('bottom-14')
        videoFloaterContainer?.classList?.add('bottom-14')
      }
      for (const button of buttons) {
        button.classList.add('text-sm');
      }
    } else {
      const oldButtonContainerClass = heroSectionSettings.isNewVersion ? [] : ['border', 'mx-2'];
      ref.classList.add('relative', 'max-w-lg', ...oldButtonContainerClass, 'sm:mx-6', 'p-2');
      ref.classList.remove('fixed', 'top-7', 'w-full', 'z-[55]', 'left-0', 'bottom-0', 'p-1', 'pb-0', 'bg-canvas-light', 'border-t-outer', 'border-b-outer', 'border-frame', 'justify-center');
      if (subRef) subRef.style.height = ""
      whatsappChatButton?.classList.remove('bottom-14')
      videoFloaterContainer?.classList?.remove('bottom-14')
      for (const button of buttons) {
        button.classList.remove('text-sm');
      }
    }
  });

  function capturePdpCtaClick(props) {
    window.posthog?.capture('pdp_main_cta_click', {
      ...props,
      experiment_flags: window.experimentFlags
    })
  }

  return (
    <>
      <div ref={topRef} class="absolute top-0 left-0" style={{ height: '200px' }} />
      <div ref={subRef} />
      <div
        ref={ref}
        class={twMerge(
          "flex flex-col items-center",
          props.class ?? ""
        )}
      >
        <div
          class="flex font-bold uppercase text-lg sm:text-base max-w-lg w-full gap-1"
          style="transition: position 0.5s ease-in-out;"
          classList={{
            // "gap-2": props?.isModal != true,
            "flex-col items-center": props?.isModal == true
          }}
        >
          <Show
            when={!isOutOfStock()}
            fallback={
              <Show
                when={props.isModal != true}
                fallback={(
                  <div class="flex items-center justify-around px-2 w-full">
                    <button
                      type="button"
                      onClick={() => window.location = `/products/${productHandle}?color=${selectedColor()}`}
                      class="text-base  text-ink flex underline hover:no-underline justify-center items-center rounded-lg "
                    >
                      See full details
                    </button>
                    <button
                      type="button"
                      onClick={() => window.location = `/products/${productHandle}?color=${selectedColor()}`}
                      class="text-base  text-ink flex underline hover:no-underline justify-center items-center rounded-lg "
                    >
                      Customize
                    </button>
                  </div>
                )}
              >
                <button
                  type="button"
                  onClick={() => {
                    capturePdpCtaClick({
                      buttonText: 'Notify Me'
                    })
                    handleSubmit('notifyMe')
                  }}
                  class="py-2 font-bold px-4 cta-color hover:catalog-bg-light-color border border-frame grow text-secondary-100 flex justify-center items-center rounded-xl"
                >
                  Notify me
                </button>
              </Show>
            }
          >
            <Show
              when={heroSectionSettings.isNewVersion}
              fallback={(
                <button
                  type="button"
                  id="add-to-bag"
                  onClick={async () => {
                    capturePdpCtaClick({
                      buttonText: showPreSale() && productInfo?.presale?.cta_text ? productInfo?.presale?.cta_text : 'Add to bag',
                      viewedBrandComparison: window.isBrandComparisonVisited ?? false,
                      viewedBrandValues: window.isBrandValuesVisited ?? false,
                      viewedRTBBelowCTA: window.isRTBBelowCTAVisited ?? false
                    })
                    await handleSubmit('addToCart')
                    if (props.isModal) {
                      window.closeQuickShop();
                      document.body.style.overflow = 'hidden';
                    }
                  }}
                  class="p-2 border border-solid grow text-base hover:catalog-bg-light-color text-canvas flex justify-center items-center rounded-lg  hover:text-canvas-light bg-ink"
                  classList={{
                    "w-full": props.isModal == true,
                    "px-1": showPreSale() && productInfo?.presale?.cta_text
                  }}
                >
                  {addToCartLoading()
                    ? CustomLoader('#000')
                    : showPreSale() && productInfo?.presale?.cta_text ? productInfo?.presale?.cta_text : `Add to bag`}
                </button>
              )}
            >

              <NeumorphicAddToBagButton
                id="add-to-bag"
                buttonClass={`${showPreSale() && productInfo?.presale?.cta_text ? 'px-1' : 'px-2'} py-2 border border-solid grow text-base flex justify-center items-center `}
                buttonText={showPreSale() && productInfo?.presale?.cta_text ? productInfo?.presale?.cta_text : `Add to bag`}
                handleAddToBagClick={async () => {
                  capturePdpCtaClick({
                    buttonText: showPreSale() && productInfo?.presale?.cta_text ? productInfo?.presale?.cta_text : 'Add to bag',
                    viewedBrandComparison: window.isBrandComparisonVisited ?? false,
                    viewedBrandValues: window.isBrandValuesVisited ?? false,
                    viewedRTBBelowCTA: window.isRTBBelowCTAVisited ?? false
                  })
                  await handleSubmit('addToCart')
                  if (props.isModal) {
                    window.closeQuickShop();
                    document.body.style.overflow = 'hidden';
                  }
                }}
              />
            </Show>
            <StyleCustomizationModal
              buttonText={showPreSale() && productInfo?.presale?.cta_text ? productInfo?.presale?.cta_text : 'Add to bag'}
              clickHandler={() => {
                capturePdpCtaClick({
                  buttonText: 'Customize',
                  viewedBrandComparison: window.isBrandComparisonVisited ?? false,
                  viewedBrandValues: window.isBrandValuesVisited ?? false,
                  viewedRTBBelowCTA: window.isRTBBelowCTAVisited ?? false
                })
              }}
              addToBagCallback={() => {
                capturePdpCtaClick({
                  buttonText: showPreSale() && productInfo?.presale?.cta_text ? productInfo?.presale?.cta_text : 'Add to bag',
                  viewedBrandComparison: window.isBrandComparisonVisited ?? false,
                  viewedBrandValues: window.isBrandValuesVisited ?? false,
                  viewedRTBBelowCTA: window.isRTBBelowCTAVisited ?? false
                })
              }}
            />
          </Show>
        </div>
        <LazyPaymentIconsGroup class="max-w-lg w-full mt-0.5" secureCheckoutClass="gap-0" />
      </div>
    </>
  )
}